import { useEffect, useRef, useState, useCallback } from 'react';
import { wrap, type Remote } from 'comlink';
import { WORKER_CONFIG } from '../constants/worker';
import { sleep } from '../utils/workerHelpers';
import { sentryLogException } from '../services/sentryLogger';

/**
 * React hook for using Comlink web workers
 * Replaces @shopify/react-web-worker with React 19 support
 * 
 * Note: Comlink proxies cannot be stored in React state (they're not serializable).
 * We store the proxy in a ref and use a boolean state flag to trigger re-renders.
 */
export function useComlinkWorker<T extends Record<string, any>>(
  workerImport: () => Promise<{ default: new () => Worker }>
): Remote<T> | null {
  const workerProxyRef = useRef<Remote<T> | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const [workerReady, setWorkerReady] = useState(false);

  /**
   * Verify worker API is accessible
   * @param wrapped - The Comlink-wrapped worker proxy
   * @param requiredMethod - Optional method name that must be accessible for verification to pass
   */
  const verifyWorkerAPI = useCallback(async (
    wrapped: Remote<T>,
    requiredMethod?: string
  ): Promise<{ verified: boolean; methods: string[] }> => {
    for (let attempt = 0; attempt < WORKER_CONFIG.MAX_VERIFICATION_ATTEMPTS; attempt++) {
      try {
        const getMethods = (wrapped as any).getApiMethods;
        if (!getMethods) {
          await sleep(WORKER_CONFIG.VERIFICATION_RETRY_DELAY);
          continue;
        }

        const methods = await getMethods() as string[];

        // No required method check - verification complete
        if (!requiredMethod) {
          return { verified: true, methods };
        }

        // Verify required method exists in API and is accessible
        const isMethodInApi = methods.includes(requiredMethod);
        const isMethodAccessible = (wrapped as any)[requiredMethod] != null;

        if (isMethodInApi && isMethodAccessible) {
          return { verified: true, methods };
        }

        // Method not ready, retry
        await sleep(WORKER_CONFIG.VERIFICATION_RETRY_DELAY);
      } catch {
        // Retry on error (worker might not be ready yet)
        if (attempt < WORKER_CONFIG.MAX_VERIFICATION_ATTEMPTS - 1) {
          await sleep(WORKER_CONFIG.VERIFICATION_RETRY_DELAY);
        }
      }
    }

    return { verified: false, methods: [] };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initWorker = async () => {
      try {
        const workerModule = await workerImport();
        const WorkerConstructor = workerModule.default;
        
        if (!WorkerConstructor) {
          throw new Error('Worker module does not export a default Worker constructor');
        }
        
        // Create actual Worker instance
        const workerInstance = new WorkerConstructor();
        workerRef.current = workerInstance;
        
        const wrapped = wrap<T>(workerInstance);
        workerProxyRef.current = wrapped;
        
        // Wait for worker script to load and expose API
        await sleep(WORKER_CONFIG.INITIALIZATION_DELAY);
        
        // Verify worker API (check for loadModel as it's commonly needed)
        const { verified, methods } = await verifyWorkerAPI(wrapped, 'loadModel');
        
        if (isMounted) {
          setWorkerReady(verified);
          if (verified) {
            console.log('Worker initialized successfully', { methods });
          } else {
            console.warn('Worker initialized but API verification failed', { methods });
          }
        }
      } catch (error) {
        console.error('Failed to create worker:', error);
        sentryLogException({
          error,
          tags: {
            component: 'useComlinkWorker',
            category: 'workerCreation',
          },
          extra: {
            timestamp: new Date().toISOString(),
          },
        });
        if (isMounted) {
          workerProxyRef.current = null;
          setWorkerReady(false);
        }
      }
    };

    initWorker();

    return () => {
      isMounted = false;
      workerRef.current?.terminate();
      workerRef.current = null;
      workerProxyRef.current = null;
    };
  }, [workerImport, verifyWorkerAPI]);

  return workerReady ? workerProxyRef.current : null;
}

/**
 * Creates a worker factory function (for API compatibility with @shopify/react-web-worker)
 */
export function createComlinkWorkerFactory<T extends Record<string, any>>(
  moduleLoader: () => Promise<T>
): () => Promise<{ default: new () => Worker }> {
  try {
    if(moduleLoader.toString().includes('tensorflow')) {
      return () => import('../workers/tensorflow.worker.ts?worker');
    } else {
      throw new Error(`Unknown module path: ${moduleLoader}`);
    }
  } catch (error) {
    throw new Error(`Unknown module path: ${moduleLoader}`);
  }
}
